/**
 * usePermissions Composable
 *
 * Route-based permission system. Route names ARE the permission keys.
 * The router is the single source of truth — no manual permission mapping needed.
 *
 * Provides:
 * - canAccessRoute(routeName) — check a specific route
 * - canAccessGroup(groupName) — check if user can access ANY route in a permissionGroup
 * - hasPermission(key) — check any permission key (route or action)
 * - Action-specific computeds: canImpersonate, canDeleteProjects, canAccessWorkPool, canSwitchUsersOnMyWork
 */

import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useUserAuthStore } from '@/stores/userAuthStore';
import {
  SYSTEM_ROLES,
  ACTION_PERMISSIONS,
  migratePermissions,
  getAllPermissionKeys
} from '@/constants/permissions';

export function usePermissions() {
  const userAuthStore = useUserAuthStore();
  const router = useRouter();

  const isSuperAdmin = computed(() => {
    return userAuthStore.user?.systemRole === SYSTEM_ROLES.SUPERADMIN;
  });

  const isAdminUser = computed(() => {
    const role = userAuthStore.user?.systemRole;
    return role === SYSTEM_ROLES.ADMIN || role === SYSTEM_ROLES.SUPERADMIN;
  });

  // Runtime migration: convert old-format permissions transparently
  const userPermissions = computed(() => {
    if (isSuperAdmin.value) return getAllPermissionKeys(router);
    const raw = userAuthStore.user?.permissions || [];
    return migratePermissions(raw);
  });

  /**
   * Check if user has a specific permission key (route name or action key)
   * @param {string} key
   * @returns {boolean}
   */
  const hasPermission = (key) => {
    if (isSuperAdmin.value) return true;
    return userPermissions.value.includes(key);
  };

  /**
   * Can user access a specific route by name?
   * Routes without permissionGroup are always accessible.
   * Child routes (with parentRoute) check the parent's permission.
   * @param {string} routeName
   * @param {Object} [params] - route params, required for routes with dynamic
   *   segments (e.g. the company sections need { id }). vue-router throws
   *   MissingRequiredParamError from resolve() when they're absent.
   * @returns {boolean}
   */
  const canAccessRoute = (routeName, params = {}) => {
    if (isSuperAdmin.value) return true;
    let resolved;
    try {
      resolved = router.resolve({ name: routeName, params });
    } catch {
      // Unknown route, or params missing for a dynamic segment — treat as
      // inaccessible rather than taking the whole render down.
      return false;
    }
    // Super-only routes are never accessible to non-super users, regardless of
    // any granted permissionGroup.
    if (resolved?.meta?.requiresSuper) return false;
    if (!resolved?.meta?.permissionGroup) return true;
    const keyToCheck = resolved.meta.parentRoute || routeName;
    return userPermissions.value.includes(keyToCheck);
  };

  /**
   * Can user access ANY route in a permissionGroup?
   * Useful for showing/hiding nav sections.
   * @param {string} groupName - e.g. 'Projects', 'Reports', 'Configuration'
   * @returns {boolean}
   */
  const canAccessGroup = (groupName) => {
    if (isSuperAdmin.value) return true;
    const groupRoutes = router.getRoutes().filter(r =>
      r.meta?.permissionGroup === groupName && !r.meta?.parentRoute
    );
    return groupRoutes.some(r => userPermissions.value.includes(r.name));
  };

  // Action-specific computeds
  const canImpersonate = computed(() => hasPermission('impersonation'));
  const canDeleteProjects = computed(() => hasPermission('projects.delete'));
  const canAccessWorkPool = computed(() => hasPermission('mywork.work-pool'));
  const canSwitchUsersOnMyWork = computed(() => hasPermission('mywork.switch-users'));
  const canViewUserProfiles = computed(() => hasPermission('profile.view-users'));
  const canSkipInternalReview = computed(() => hasPermission('deliverables.skip-internal-review'));

  return {
    // State
    isSuperAdmin,
    isAdminUser,
    userPermissions,

    // Methods
    hasPermission,
    canAccessRoute,
    canAccessGroup,

    // Action permission computeds
    canImpersonate,
    canDeleteProjects,
    canAccessWorkPool,
    canSwitchUsersOnMyWork,
    canViewUserProfiles,
    canSkipInternalReview,

    // Kept for backward compat — callers that reference ACTION_PERMISSIONS
    ACTION_PERMISSIONS
  };
}
